import { useMemo } from 'react'

import moment from 'moment'

import { ScheduledSessionIcon } from 'src/components/icons'
import { Button } from 'src/components/VerbitWrappers/Button'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { useOrderDetails } from 'src/contexts/OrderDetailsContext'
import { useUserContext } from 'src/contexts/UserProvider'
import { OrderStatus } from 'src/models/Order'
import { isVisorCustomer } from 'src/network/types'
import { useCurrentTimeTick } from 'src/hooks/useCurrentTimeTick'
import { useToastWithAnnouncement } from 'src/hooks/useToastWithAnnouncement'
import { useVerbitConnectAvailability } from 'src/hooks/useVerbitConnectAvailability'
import { isUserWithRoles } from 'src/models/User'
import { getOrderingFrontendUrl } from 'src/utils/env'

import { calculateExtendedDuration, isMissingProductError, ROLES_THAT_CAN_EDIT_ORDER, START_EARLY_WINDOW_HOURS } from './startEarlyUtils'
import * as S from './styles'

const START_EARLY_TICK_INTERVAL = 60_000

const formatScheduledDate = (isoDate: string): string => {
    return moment(isoDate).format('MMM D, YYYY, h:mm A')
}

export const SessionScheduledTemplate = () => {
    const { order, updateOrder, isUpdating } = useOrderDetails()
    const { user, customer } = useUserContext()
    const { isMediaRecorderAvailable } = useVerbitConnectAvailability()
    const isStartEarlyEnabled = useFeatureFlag('start_early')
    const currentTime = useCurrentTimeTick(START_EARLY_TICK_INTERVAL)
    const showToast = useToastWithAnnouncement()

    const schedule = order?.input.schedule
    const scheduledStartTime = schedule?.start_at

    const canStartEarly = useMemo(() => {
        if (!isStartEarlyEnabled || !isMediaRecorderAvailable || !scheduledStartTime) {
            return false
        }

        if (order?.status.name === OrderStatus.ReadyToConnect || order?.status.name === OrderStatus.InProgress) {
            return false
        }

        if (!isUserWithRoles(user, ROLES_THAT_CAN_EDIT_ORDER)) {
            return false
        }

        const windowStartMs = new Date(scheduledStartTime).getTime() - START_EARLY_WINDOW_HOURS * 60 * 60 * 1000

        return currentTime.getTime() >= windowStartMs
    }, [isStartEarlyEnabled, isMediaRecorderAvailable, scheduledStartTime, order, user, currentTime])

    const handleStartEarly = async () => {
        if (!scheduledStartTime || !schedule?.max_duration) {
            return
        }

        const newStartAt = new Date().toISOString()
        const newDuration = calculateExtendedDuration(scheduledStartTime, newStartAt, schedule.max_duration)

        try {
            await updateOrder({
                input: {
                    schedule: {
                        start_at: newStartAt,
                        max_duration: newDuration,
                    },
                },
            })

            showToast({ type: 'success', title: 'Session started' })
        } catch (error) {
            const description = isMissingProductError(error)
                ? 'You cannot change the session start time this close to the scheduled start. Please contact support to enable this option for your account.'
                : "Try again or adjust the order's scheduled time to the current time."

            showToast({ type: 'error', title: 'Session start failed', description })
        }
    }

    const handleEditOrderDetails = () => {
        window.open(`${getOrderingFrontendUrl()}/orders/${order!.id}/edit`, '_blank')
    }

    return (
        <S.TemplateContent>
            <ScheduledSessionIcon />
            <S.TemplateDescription>
                {scheduledStartTime ? `Session is scheduled for ${formatScheduledDate(scheduledStartTime)}` : 'Session is scheduled'}
            </S.TemplateDescription>
            {canStartEarly && (
                <>
                    {isVisorCustomer(customer) ? (
                        <Button variant="primary" mt={4} isRounded isLoading={isUpdating} onClick={handleStartEarly}>
                            Start now instead
                        </Button>
                    ) : (
                        <Button variant="primary" mt={4} isRounded onClick={handleEditOrderDetails}>
                            Edit order details
                        </Button>
                    )}
                </>
            )}
        </S.TemplateContent>
    )
}
